import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import {
  Calendar,
  Dropdown,
  DefaultButton,
  TextField,
} from '@fluentui/react';
import { Icon } from '@fluentui/react/lib/Icon';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  validateEmailAddress,
  validatePhone,
} from '@bingads-webui-campaign-react/smart-component-util';
import { RadioButton as Availability } from '@bingads-webui-campaign-react/smart-component-primitive';

import { SmartBookingModal } from './booking-modal-base';

const getStyles = () => ({
  container: {
    width: '100%',
    position: 'relative',
  },
  step: {
    width: '100%',
    padding: '20px 70px',
  },
  duration: {
    marginBottom: 30,
  },
  timePicker: {
    display: 'flex',

    '& .card th': {
      textAlign: 'center',
    },

    '& .indicator': {
      display: 'flex',
      alignItems: 'center',
      height: 30,
      marginBottom: 8,
      fontWeight: 600,

      '& i': {
        fontWeight: 600,
      },

      '& span': {
        marginLeft: 5,
        marginTop: '-2px',
      },
    },
  },
  availability: {
    paddingLeft: 25,
  },
  stepTwo: {
    padding: '0 72px',
  },
  form: {
    marginTop: 20,

    '& .ms-TextField-fieldGroup': {
      border: '1px solid #B3B0AD',
    },

    '& .name': {
      display: 'flex',
      justifyContent: 'space-between',

      '& .ms-TextField': {
        width: 202,
      },
    },
  },
  item: {
    minHeight: 85,
  },
  bold: {
    fontWeight: 600,
  },
  error: {
    color: 'red',
    position: 'absolute',
    bottom: 0,
  },
});

const customModalStyle = {
  main: {
    width: '590px',
    height: '680px',
  },
};

const textAreaStyle = {
  fieldGroup: {
    height: 134,
  },
};

const editButtonStyle = {
  root: {
    minWidth: 60,
    marginLeft: 20,
    height: 26,
  },
};

const calenderStyle = {
  root: {
    boxShadow: '0 3px 10px -5px grey',
  },
};

// Currently, only support two types of fixed duration.
const options = [
  { key: '30min', text: '30min' },
  { key: '1hour', text: '1hour' },
];

// TODO: For test now, will use dynamic data later.
const availability = [
  { key: '12:00 PM', text: '12:00 PM' },
  { key: '12:30 PM', text: '12:30 PM' },
  { key: '1:00 PM', text: '11:00 PM' },
  { key: '1:30 PM', text: '1:30 PM' },
];

const STEP_CONFIG = {
  ONE: {
    key: 'ONE',
    buttonText: _TL_('Continue'),
    validator: ({ time }, i18n) => {
      if (!time) {
        return i18n.getString(_TL_('Please select specific time'));
      }

      return null;
    },
  },
  TWO: {
    key: 'TWO',
    buttonText: _TL_('Submit'),
    validator: ({ email, phoneNumber }, i18n) => {
      const emailError = validateEmailAddress(email) ? '' : i18n.getString(_TL_('Invalid email address'));
      const phoneError = validatePhone(phoneNumber) ? '' : i18n.getString(_TL_('Invalid phone number'));

      if (!emailError && !phoneError) {
        return null;
      }

      return {
        email: emailError,
        phoneNumber: phoneError,
      };
    },
  },
};

const UstyledBookingSteps = ({
  classes,
  i18n,
  isOpen,
  onClose,
}) => {
  const [currentStep, setCurrentStep] = useState(STEP_CONFIG.ONE);
  // Step one:
  const [duration, setDuration] = useState(options[0]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState({});
  const [stepOneError, setStepOneError] = useState(null);
  // Step two:
  const [name, setName] = useState({ firstName: '', lastName: '' });
  const [email, setEmail] = useState({ value: '', error: '' });
  const [phoneNumber, setPhoneNumber] = useState({ value: '', error: '' });
  const [extraMessage, setExtraMessage] = useState('');

  const formattedBookingInfo = `${moment(selectedDate).format('MMMM Do YYYY')}, ${duration.text}, ${selectedTime.text}`;

  const onSelectDate = useCallback((date) => {
    setSelectedDate(date);
  }, []);

  const onSelectDuration = useCallback((e, item) => {
    setDuration(item);
  }, []);

  const onSelectTime = useCallback((time) => {
    setSelectedTime(time);
    setStepOneError(null);
  }, []);

  const onChangeName = useCallback((e, newValue, key) => {
    setName(prev => ({
      ...prev,
      [key]: newValue,
    }));
  }, []);

  const onChangeEmail = useCallback((e, newValue) => {
    setEmail({
      value: newValue,
      error: validateEmailAddress(newValue)
        ? ''
        : i18n.getString(_TL_('Invalid email address')),
    });
  }, [i18n]);

  const onChangePhoneNumber = useCallback((e, newValue) => {
    setPhoneNumber({
      value: newValue,
      error: validatePhone(newValue)
        ? ''
        : i18n.getString(_TL_('Invalid phone number')),
    });
  }, [i18n]);

  const onChangeExtraMessage = useCallback((e, newValue) => {
    setExtraMessage(newValue);
  }, []);

  const onClickFooterBtn = useCallback(() => {
    if (currentStep.key === STEP_CONFIG.ONE.key) {
      const validationResults = currentStep.validator({ time: selectedTime.text }, i18n);
      setStepOneError(validationResults);

      if (!validationResults) {
        setCurrentStep(STEP_CONFIG.TWO);
      }
    } else {
      const validationResults = currentStep.validator({
        email: email.value,
        phoneNumber: phoneNumber.value,
      }, i18n);

      if (!validationResults) {
        // TODO: send call
      } else {
        setEmail(prev => ({ ...prev, error: validationResults.email }));
        setPhoneNumber(prev => ({ ...prev, error: validationResults.phoneNumber }));
      }
    }
  }, [currentStep, email, phoneNumber, selectedTime, i18n]);

  const onBackToStepOne = useCallback(() => {
    setCurrentStep(STEP_CONFIG.ONE);
  }, []);

  const stepOne = useMemo(() => (
    <div className={classNames(classes.step, 'step-one')}>
      <div className={classes.duration}>
        <Dropdown
          label={i18n.getString(_TL_('Appointment duration'))}
          options={options}
          selectedKey={duration.key}
          onChange={onSelectDuration}
        />
      </div>
      <div className={classNames(classes.timePicker, 'time-picker')}>
        <div className={classNames('card', classes.day)}>
          <div className="indicator">
            <Icon iconName="Calendar" />
            <span>{i18n.getString(_TL_('Date'))}</span>
          </div>
          <Calendar
            showMonthPickerAsOverlay
            highlightSelectedMonth
            showGoToToday={false}
            onSelectDate={onSelectDate}
            value={selectedDate}
            styles={calenderStyle}
          />
        </div>
        <div className={classNames('card', classes.availability)}>
          <div className="indicator">
            <Icon iconName="TimePicker" />
            <span>{i18n.getString(_TL_('Time'))}</span>
          </div>
          <div className="time-list">
            <Availability
              options={availability}
              selectedItem={selectedTime}
              onSelectItem={onSelectTime}
            />
          </div>
        </div>
      </div>
      {
        stepOneError && (
          <div className={classNames(classes.error, 'step-one-error')}>
            <span>*{i18n.getString(stepOneError)}</span>
          </div>
        )
      }
    </div>
  ), [
    classes, duration.key, i18n, selectedDate, selectedTime,
    onSelectDate, onSelectDuration, onSelectTime, stepOneError,
  ]);

  const stepTwo = useMemo(() => (
    <div className={classNames(classes.step, classes.stepTwo, 'step-two')}>
      <div className="appointment-detail">
        <div className={classes.bold}>{i18n.getString(_TL_('Appointment detail'))}</div>
        <div className="info">
          <span>{formattedBookingInfo}</span>
          <DefaultButton
            text={i18n.getString(_TL_('Edit'))}
            onClick={onBackToStepOne}
            styles={editButtonStyle}
          />
        </div>
      </div>
      <div className={classes.form}>
        <div className={classNames(classes.item, 'name')}>
          <TextField
            className="input-first-name"
            label={i18n.getString(_TL_('First name'))}
            value={name.firstName}
            onChange={(e, val) => onChangeName(e, val, 'firstName')}
          />
          <TextField
            className="input-last-name"
            label={i18n.getString(_TL_('Last name'))}
            value={name.lastName}
            onChange={(e, val) => onChangeName(e, val, 'lastName')}
          />
        </div>
        <div className={classes.item}>
          <TextField
            className="input-email"
            label={i18n.getString(_TL_('Email address'))}
            value={email.value}
            onChange={onChangeEmail}
            errorMessage={email.error}
          />
        </div>
        <div className={classes.item}>
          <TextField
            className="input-phone"
            label={i18n.getString(_TL_('Phone number'))}
            value={phoneNumber.value}
            onChange={onChangePhoneNumber}
            errorMessage={phoneNumber.error}
          />
        </div>
        <div className={classes.item}>
          <TextField
            className="input-message"
            label={i18n.getString(_TL_('Message'))}
            value={extraMessage}
            onChange={onChangeExtraMessage}
            resizable={false}
            styles={textAreaStyle}
            multiline
          />
        </div>
      </div>
    </div>
  ), [
    classes, email, extraMessage, i18n, name, phoneNumber, formattedBookingInfo,
    onBackToStepOne, onChangeEmail, onChangeExtraMessage, onChangeName, onChangePhoneNumber,
  ]);

  return (
    <SmartBookingModal
      i18n={i18n}
      isOpen={isOpen}
      title={i18n.getString(_TL_('Book Appointment'))}
      actionButtonLabel={i18n.getString(currentStep.buttonText)}
      onClickFooterButton={onClickFooterBtn}
      onCloseModal={onClose}
      modalStyle={customModalStyle}
    >
      <div className={classNames(classes.container, 'booking-steps-container')}>
        {
          currentStep.key === STEP_CONFIG.ONE.key ? stepOne : stepTwo
        }
      </div>
    </SmartBookingModal>
  );
};

UstyledBookingSteps.propTypes = {
  classes: PropTypes.shape({
    container: PropTypes.string.isRequired,
    step: PropTypes.string.isRequired,
    duration: PropTypes.string.isRequired,
    timePicker: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export const BookingSteps = withDefaultStyles(
  UstyledBookingSteps,
  getStyles
);
